import { useState } from "react";

/**
 * Copies `text` to the clipboard via the async Clipboard API, falling back to a hidden textarea
 * + execCommand for older browsers and contexts that block the async API. Resolves true on success.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to execCommand path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function CopyWoButton({ workOrderNumber }: { workOrderNumber: string | null }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const disabled = !workOrderNumber;

  const onClick = async () => {
    const ok = await copyToClipboard(workOrderNumber ?? "");
    setState(ok ? "copied" : "failed");
    window.setTimeout(() => setState("idle"), 1800);
  };

  const label = state === "copied" ? "Copied!" : state === "failed" ? "Copy failed" : "Copy WO #";
  const icon = state === "copied" ? "check_circle" : state === "failed" ? "error" : "content_copy";

  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "No WO # to copy" : `Copy "${workOrderNumber}" to clipboard`}
      aria-live="polite"
    >
      <span className="material-symbols-rounded">{icon}</span>
      {label}
    </button>
  );
}
